import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'store';
import { handleSettingsResponse, updateSettings } from './common-settings';

interface TargetPayGapSettingsProps {
  targetPayGap?: string | null;
}

export const TargetPayGapSettings = ({ targetPayGap }: TargetPayGapSettingsProps) => {
  const { t } = useTranslation();
  const { updateSpinnerShow } = useCommonStore();

  const [targetGap, setTargetGap] = useState<string>(targetPayGap ?? '0');
  const [error, setError] = useState<string>('');
  
  // Refs for debouncing and tracking latest value
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef<string>(targetGap);

  // Update targetGap when prop changes
  useEffect(() => {
    setTargetGap(targetPayGap ?? '0');
    latestValueRef.current = targetPayGap ?? '0';
  }, [targetPayGap]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const updateTargetGap = useCallback(async (value: string) => {
    // Only proceed if this value is still the latest
    if (value !== latestValueRef.current) return;
    
    try {
      const response = await updateSettings({ target_pay_gap: value });
      // Only handle response if value is still current
      if (value === latestValueRef.current) {
        handleSettingsResponse(response);
      }
    } catch (err) {
      console.error('Error updating target pay gap:', err);
    } 
  }, []);

  const debouncedUpdateTargetGap = useCallback((value: string) => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout - API call after 500ms of no typing
    debounceTimeoutRef.current = setTimeout(() => {
      updateTargetGap(value);
    }, 500);
  }, [updateTargetGap]);

  const handleTargetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Block if more than one decimal point
    if ((value.match(/\./g) || []).length > 1) return;

    // Allow empty, single "-", or intermediate decimal states while typing
    if (value === '' || value === '-' || value === '.' || value === '-.') {
      setTargetGap(value);
      latestValueRef.current = value;
      setError('');
      return;
    }

    // Handle values ending with decimal point (e.g., "-74.")
    if (value.endsWith('.')) {
      // Validate the numeric part before the decimal
      const numPart = value.slice(0, -1);
      const num = Number(numPart);
      
      if (!isNaN(num) && (num < -20 || num > 0)) {
        setTargetGap(value);
        latestValueRef.current = value;
        setError(t('pe.targetPayGapSettings.validationError'));
        return;
      }
      
      setTargetGap(value);
      latestValueRef.current = value;
      setError('');
      return;
    }

    // Allow numeric characters with optional minus sign and one decimal place
    if (!/^-?\d*\.?\d?$/.test(value)) return;

    setTargetGap(value);
    latestValueRef.current = value;

    const num = Number(value);

    // Disallow "-0" explicitly (num is 0 but starts with "-")
    if (num === 0 && value.startsWith('-')) {
      setError(t('pe.targetPayGapSettings.validationError'));
      return;
    }

    // Range validation
    if (num < -20 || num > 0) {
      setError(t('pe.targetPayGapSettings.validationError'));
    } else {
      setError('');
      debouncedUpdateTargetGap(value);
    }
  };

  const handleBlur = () => {
    // Clear any pending debounced calls
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    const num = Number(targetGap);

    if (targetGap === '' || targetGap === '-' || targetGap === '.' || targetGap === '-.' || targetGap === '-0' || targetGap.endsWith('.') || isNaN(num) || num < -20 || num > 0) {
      setTargetGap('0');
      latestValueRef.current = '0';
      setError('');
      updateTargetGap('0');
    } else {
      // If valid value, make immediate API call on blur
      latestValueRef.current = targetGap;
      updateTargetGap(targetGap);
    }
  };

  return (
    <div className="mb-6">
      <div className="mt-6 rounded-[5px] border border-dotted border-ods-color-brand-primary-400 px-[1.5rem] py-[1.75rem]">
        <div className="flex items-start justify-between">
          <div className="flex w-[60%] flex-col font-ods-font-family-secondary text-ods-font-styles-body-regular-small text-ods-color-brand-neutral-black">
            <div className="mb-6 font-bold">
              {t('pe.targetPayGapSettings.header')}
            </div>
            <div className="font-normal">
              {t('pe.targetPayGapSettings.description')}
            </div>
          </div>

          <div className="flex w-[40%] items-center justify-end">
            <label className="mr-4 font-ods-font-family-secondary text-ods-font-styles-body-regular-medium font-semibold text-ods-color-brand-neutral-900">
              {t('pe.targetPayGapSettings.label')}
            </label>

            <div className="relative inline-block">
              <div className="flex items-center">
                <input
                  type="text"
                  value={targetGap}
                  onChange={handleTargetChange}
                  onBlur={handleBlur}
                  placeholder={t('pe.entervalue')}
                  className={`w-[120px] rounded px-3 py-2 text-center
                    font-ods-font-family-secondary text-ods-font-styles-body-regular-small
                    focus:outline-none focus:ring-0
                    ${
                      error
                        ? 'border border-red-600 focus:border-red-600'
                        : 'border border-ods-color-brand-neutral-300 focus:border-ods-color-brand-neutral-300'
                    }
                  `}
                  maxLength={5}
                />
                <span className="ml-2 font-ods-font-family-secondary text-ods-font-styles-body-regular-medium font-semibold text-ods-color-brand-neutral-900">
                  %
                </span>
              </div>

              {error && (
                <div className="absolute top-full mt-2 right-1 w-max whitespace-nowrap text-red-600">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};